import React, { useState } from 'react';
import { PUBLIC_CONTACT_EMAIL } from '../../constants/landingPublicContact';
import styles from './PrivacyPolicy.module.css';

interface PrivacyPolicyProps {
  onAccept?: (accepted: boolean) => void;
  showCheckbox?: boolean;
  className?: string;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ 
  onAccept, 
  showCheckbox = false,
  className = '' 
}) => {
  const [accepted, setAccepted] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isAccepted = e.target.checked;
    setAccepted(isAccepted);
    if (onAccept) {
      onAccept(isAccepted);
    }
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <div className={className}>
        {showCheckbox ? (
          <div className={styles.privacyContainer}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={accepted}
                onChange={handleCheckboxChange}
                className={styles.checkbox}
              />
              <span className={styles.checkboxText}>
                I have read and agree to the{' '}
                <button 
                  type="button"
                  onClick={handleLinkClick}
                  className={styles.privacyLink}
                >
                  Privacy Policy
                </button>
              </span>
            </label>
          </div>
        ) : (
          <a 
            href="#"
            onClick={handleLinkClick}
            style={{ 
              color: '#fff', 
              textDecoration: 'none', 
              fontSize: '0.95rem',
              lineHeight: '1'
            }}
          >
            Privacy Policy
          </a>
        )}
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Privacy Policy</h2>
              <button 
                className={styles.closeButton}
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.section}>
                <p><strong>Last Updated:</strong> January 8, 2026</p>
                
                <p>
                  This Privacy Policy describes how Tranzit.io ("Tranzit," "we," "us," or "our") collects, uses, discloses,
                  and protects information when you access or use our platform, website, and related services (the
                  "Platform"). Tranzit operates as a logistics visibility and information platform. We are not a motor
                  carrier, freight broker, or freight forwarder.
                </p>

                <h3>1. Information We Collect</h3>
                <p>
                  We collect information you voluntarily provide and information generated through use of the
                  Platform, including personal, business, location, communication, usage, and device data.
                </p>

                <h3>2. How We Use Information</h3>
                <p>
                  Information is used to operate and improve the Platform, enable user-initiated connections, provide
                  visibility features, communicate with users, prevent fraud, and comply with legal obligations. Tranzit
                  does not arrange transportation or participate in shipments.
                </p>

                <h3>3. Automated Processing and AI</h3>
                <p>
                  Certain features use automated or algorithmic processes to generate informational insights.
                  Outputs are not guarantees or professional advice.
                </p>

                <h3>4. Information Sharing</h3>
                <p>
                  Information may be shared with other users at your direction, with service providers supporting
                  platform operations, or when required by law. Tranzit does not hold or escrow freight funds.
                </p>

                <h3>5. Location Data</h3>
                <p>
                  Location data is collected only when enabled and may be disabled at any time through settings or
                  device permissions.
                </p>

                <h3>6. Data Security</h3>
                <p>
                  We implement reasonable administrative and technical safeguards but cannot guarantee absolute
                  security.
                </p>

                <h3>7. Data Retention</h3>
                <p>
                  Information is retained only as necessary for operations, legal compliance, security, and dispute
                  resolution.
                </p>

                <h3>8. Your Rights</h3>
                <p>
                  You may request access, correction, deletion, portability, or restriction of processing subject to legal
                  limitations.
                </p>

                <h3>9. Cookies</h3>
                <p>
                  Cookies are used to maintain sessions, analyze usage, and improve functionality.
                </p>

                <h3>10. Third-Party Services</h3>
                <p>
                  We are not responsible for the privacy practices of third-party websites or services.
                </p>

                <h3>11. International Transfers</h3>
                <p>
                  Data may be processed outside your jurisdiction with appropriate safeguards.
                </p>

                <h3>12. Children's Privacy</h3>
                <p>
                  The Platform is not intended for individuals under the age permitted by applicable law.
                </p>

                <h3>13. Changes</h3>
                <p>
                  Updates will be posted with a revised "Last Updated" date.
                </p>

                <h3>14. Contact</h3>
                <p>
                  Email: {PUBLIC_CONTACT_EMAIL}<br />
                  Houston, Texas, United States
                </p>
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              {showCheckbox && (
                <button 
                  className={styles.acceptButton}
                  onClick={() => {
                    setAccepted(true);
                    if (onAccept) {
                      onAccept(true);
                    }
                    closeModal();
                  }}
                >
                  I Accept
                </button>
              )}
              <button 
                className={styles.closeButtonFooter}
                onClick={closeModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PrivacyPolicy;
